import {
  API_RESOURCE_CONFLICT,
  type PoliciesGetResponseBody,
  type PolicyId,
  type RoleGetResponseBody,
  type RoleId,
  type RolesPostRequestBody,
  type RolesPostResponseBody,
} from '@ipfabric/utils';
import { Badge } from 'components/Badge/Badge';
import { Button } from 'components/Button/Button';
import { FormField } from 'components/FormField/FormField';
import { FormLayout } from 'components/FormLayout/FormLayout';
import { FormikMultiSelectField } from 'components/FormikMultiSelectField/FormikMultiSelectField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';

import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { SelectOptionType } from 'components/Select/SelectOption';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { type FormikErrors, useFormik } from 'formik';
import { array } from 'fp-ts';
import { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { validateRoleName } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';

export const newRole = Symbol('new role');

interface RoleFormData {
  name: string;
  description: string;
  policyIds: PolicyId[];
}

interface Props {
  copiedRoleId: RoleId | null;
  editedRoleId: RoleId | typeof newRole | null;
  onClose?: () => void;
}

const emptyValues: RoleFormData = {
  name: '',
  description: '',
  policyIds: array.empty,
};

interface ReadonlyPolicyNamesProps {
  policyIds: PolicyId[];
  policyOptions: SelectOptionType<PolicyId>[];
}

const policySort = (
  a: SelectOptionType<PolicyId>,
  b: SelectOptionType<PolicyId>,
) => a.label.localeCompare(b.label);
const ReadonlyPolicyNames: FC<ReadonlyPolicyNamesProps> = (props) => {
  const policies = useMemo(
    () =>
      props.policyOptions.filter((option) =>
        props.policyIds.includes(option.value),
      ),
    [props.policyIds, props.policyOptions],
  );
  return (
    <>
      {policies.map(({ label, value }) => (
        <Badge className='m-1' key={value}>
          {label}
        </Badge>
      ))}
    </>
  );
};

export const RoleForm: FC<Props> = (props) => {
  const api = useApi();
  const { showNotification } = useNotifications();
  const [initialValues, setInitialValues] = useState<RoleFormData>(emptyValues);
  const [readonly, setReadonly] = useState<boolean>(false);
  const [saving, setSaving] = useLoadingState();
  const [loading, setLoading] = useLoadingState();
  const [policies, setPolicies] = useState<SelectOptionType<PolicyId>[]>(
    array.empty,
  );
  const {
    administrationRolesMessages: { form: formMsg, request: requestMsg },
  } = useLocale();

  const loadPolicies = useStableCallback(async () => {
    const policiesResponse = await api.request<PoliciesGetResponseBody>({
      url: '/policies',
      method: 'GET',
    });
    setPolicies(
      policiesResponse.data
        .map((policy) => ({
          label: policy.name,
          value: policy.id,
          tooltip: policy.description,
        }))
        .sort(policySort),
    );
  });

  const loadRole = useStableCallback(async () => {
    if (
      props.editedRoleId !== newRole &&
      (props.editedRoleId !== null || props.copiedRoleId !== null)
    ) {
      const roleResponse = await api.request<RoleGetResponseBody>({
        url: `/roles/${encodeURIComponent(props.editedRoleId || props.copiedRoleId!)}`,
        method: 'GET',
      });
      setInitialValues({
        name:
          props.copiedRoleId === null
            ? roleResponse.name
            : `${roleResponse.name}_${formMsg.copy}`,
        description: roleResponse.description ?? '',
        policyIds: roleResponse.policyIds ?? array.empty,
      });
      setReadonly(!!roleResponse.isSystem);
    }
  });

  const saveRole = async (data: RoleFormData) => {
    await withLoadingIndicator(setSaving)(async () => {
      if (props.editedRoleId === newRole || props.copiedRoleId) {
        await api.request<RolesPostResponseBody, RolesPostRequestBody>({
          url: '/roles',
          method: 'POST',
          data,
        });
      } else if (props.editedRoleId !== null) {
        await api.request<RolesPostResponseBody, RolesPostRequestBody>({
          url: `/roles/${props.editedRoleId}`,
          method: 'PUT',
          data,
        });
      }
    });
  };

  useEffect(() => {
    withLoadingIndicator(setLoading)(async () =>
      Promise.all([loadPolicies(), loadRole()]),
    );
  }, []);

  const formik = useFormik<RoleFormData>({
    onSubmit: makeFormikOnSubmit(async (data) => {
      try {
        await saveRole(data);
        props.onClose?.();
      } catch (e) {
        if (isIpfError(e, 'API') && e.code === API_RESOURCE_CONFLICT) {
          showNotification({
            message: requestMsg.resourceConflict,
            type: 'danger',
          });
        } else {
          throw e;
        }
      }
    }),
    initialValues,
    enableReinitialize: true,
    validate: (values) => {
      const errors: FormikErrors<RoleFormData> = {};

      validateRoleName(values, errors, {
        pattern: formMsg.validation.pattern,
        required: formMsg.validation.nameRequired,
      });

      return errors;
    },
  });

  if (loading === 'INITIAL' || loading === 'LOADING') {
    return <LoaderAndText />;
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormLayout noPadding>
        <FormikTextField
          label={formMsg.fields.name}
          type='text'
          formik={formik}
          name='name'
          disabled={readonly}
        />
        <FormikTextField
          label={formMsg.fields.description}
          type='text'
          formik={formik}
          name='description'
          disabled={readonly}
        />
        {readonly ? (
          <FormField label={formMsg.fields.policies}>
            <ReadonlyPolicyNames
              policyIds={formik.values.policyIds}
              policyOptions={policies}
            />
          </FormField>
        ) : (
          <FormikMultiSelectField
            label={formMsg.fields.policies}
            options={policies}
            formik={formik}
            name='policyIds'
            disabled={readonly}
            searchable
            dataTestId='role-policy-select'
          />
        )}
        <footer>
          <Button
            className='mr-2'
            type='button'
            variant='secondary'
            onClick={() => {
              props.onClose?.();
            }}
          >
            {formMsg.cancel}
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              formik.submitForm();
            }}
            loading={saving === 'LOADING'}
            disabled={readonly}
            type='submit'
            dataTestId='role-save-button'
          >
            {formMsg.save}
          </Button>
        </footer>
      </FormLayout>
    </form>
  );
};
